import { NumberUtils } from "../../Global/Base/Utils/NumberUtils";
import { Globalization } from "../../Global/Dom/Globalization";
import { IValidatableControl } from "./ControlInterfaces";
import { ElementUtils } from "./Helpers/ElementUtils";
import { ValidateErrors, ValidatingDataType } from "./Helpers/ValidityInterfaces";
import { ValidityStateHelper } from "./Helpers/ValidityStateHelper";
import { registerCustomElement } from "./Utils/CustomElementUtils";
/**
     * @includeToDoc
     */
export class NumEditBase extends HTMLInputElement implements IValidatableControl {
    /** Povolené znaky při zápisu celého čísla. */
    private static _alowedCharsWholeNumbers: Set<number>;
    /** Povolené znaky při zápisu desetinného čísla. */
    private static _alowedCharsDecimal: Set<number>;
    private static _requiredPatternWholeNumbers: RegExp;
    private static _requiredPatternDecimal: RegExp;
    /** Regulární výraz pro znak oddělovače v řetezci. Znak se nastaví podle kultury v {@link _setFormats}. */
    private static _replaceGoupSep: RegExp;
    private static _groupSep: string;
    private static _decSep: string;

    /** Příznak, zda se nastavili povolené znaky a maska v {@link _setFormats}. */
    private static _formatsInit: boolean;

    private _validityState: ValidityState | null;
    /** Příznak, zda má komponenta pracovat pouze s celými čísly */
    private _wholeNumbers: boolean;
    private _displayFormat: string;
    private _focused: boolean;
    private _attached: boolean;
    private _hasDefaultDisplayFormat: boolean; //false když byl nastaven konkrétní displayFormat
    private _originalValue: number;
    /**
     * Hodnota komponenty vyjádřená jako řetězec s hodnotou nebo prázdný
     * https://developer.mozilla.org/cs/docs/Web/API/HTMLInputElement#Properties
     */
    public override get value(): string {
        let v = this._textValue;
        let isBig = false;
        if (!v)
            return "";
        v = NumEditBase._removeFormatting(v);

        const n = +v;
        if (v.length > 16) { // "9007199254740991" Max safe num count
            isBig = true;
        }

        if (v === "::") {
            this._textValue = "";
            return v;
        }
        if (Number.isNaN(n))
            return "";
        else {
            if (isBig) {
                return v;
            }
            return n.toString();
        }
    }

    public override set value(value: string) {
        //logger.log("NumEditBase set value", value);
        // v ideálním případě by sem měly chodit stringy, ale bohužel sem chodí všechno možné, proto je tu nová metoda, která umí zpracovávat více typů
        this._processIncomingValue(value);
    }

    /**
     * Hodnota komponenty vyjádřená jako číslo nebo NaN
     * https://developer.mozilla.org/cs/docs/Web/API/HTMLInputElement#Properties
     */
    public override get valueAsNumber(): number {
        if (this.value === "")
            return Number.NaN;

        return +this.value;
    }

    public override set valueAsNumber(value: number) {
        //logger.log("NumEditBase set valueAsNumber", value);
        this._processIncomingValueAsNumber(value);
    }

    private get _textValue(): string {
        return super.value;
    }

    private set _textValue(value: string) {
        //logger.log("NumEditBase set textValue", value);
        super.value = value;
    }

    /**
     * Zda má komponenta být v režimou pouze pro čtení - needitační
     * https://developer.mozilla.org/cs/docs/Web/API/HTMLInputElement#Properties
     */
    public override set readOnly(value: boolean) {
        super.readOnly = value;

        if (this._focused)
            this._removeSeparator();
        else
            this._addSeparator();
    }

    public override get readOnly(): boolean {
        return super.readOnly;
    }

    /**
     * Stav validnosti komponenty
     * https://developer.mozilla.org/cs/docs/Web/API/HTMLInputElement#Properties
     */
    public override get validity(): ValidityState {
        if (this._validityState)
            return this._validityState;
        else
            return super.validity;
    }

    /**
     * Zda má komponenta pracovat pouze s celými čísly
     */
    public set wholeNumbers(value: boolean) {
        if (this._wholeNumbers === value)
            return;

        this._wholeNumbers = value;
        ElementUtils.toggleAttribute(this, "wholenumbers", value); // zesynchronizuje property s atributem

        if (this._hasDefaultDisplayFormat) {
            this._displayFormat = value ? NumberUtils.DEFAULT_FORMAT_GENERAL : NumberUtils.DEFAULT_FORMAT_MONEY;
        }

        this._render();
    }

    public get wholeNumbers(): boolean {
        return this._wholeNumbers;
    }

    /**
     * Formátování čísla
     * https://wiki-dokumentace/index.php?title=DisplayFormat
     */
    public set displayFormat(value: string) {
        if (this._displayFormat === value)
            return;

        this._displayFormat = !value ? NumberUtils.DEFAULT_FORMAT_REAL_NUMBER : value;
        this._hasDefaultDisplayFormat = !value;

        this.setAttribute("displayformat", this._displayFormat);
        this._render();
    }

    public get displayFormat(): string {
        return this._displayFormat;
    }

    protected static get observedAttributes(): string[] {
        return ["displayformat", "wholenumbers"];
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this._validityState = null;
        this._wholeNumbers = false;
        this._displayFormat = NumberUtils.DEFAULT_FORMAT_MONEY;
        this._hasDefaultDisplayFormat = true;
        this._focused = false;
        this._attached = false;
        this.type = "text";
        this.setAttribute("autocomplete", "off");
        this.classList.add("num-edit-base", "editable-component", "component");

        NumEditBase._setFormats();

        const v = this.getAttribute("value");
        if (v)
            this.value = v;
    }

    protected connectedCallback(): void {
        this.addEventListener("input", NumEditBase._onInput);
        this.addEventListener("keypress", NumEditBase._onKeyPress);
        this.addEventListener("focus", NumEditBase._onFocus);
        this.addEventListener("blur", NumEditBase._onBlur);

        this._attached = true;
        this._render();
    }

    protected disconnectedCallback(): void {
        this.removeEventListener("input", NumEditBase._onInput);
        this.removeEventListener("keypress", NumEditBase._onKeyPress);
        this.removeEventListener("focus", NumEditBase._onFocus);
        this.removeEventListener("blur", NumEditBase._onBlur);

        this._attached = false;
    }

    private _render(): void {
        // znovu naformátuje zobrazovanou hodnotu
        if (this._attached && this._originalValue != null)
            this.value = this._originalValue.toString();
    }

    /**
     * Inicializuje formatovaci masku a povolene znaky pro nastavenou lokalizaci.
     * Nastaví se jen jednou.
     */
    private static _setFormats(): void {
        if (NumEditBase._formatsInit)
            return;

        const format = Globalization.numberFormat;
        NumEditBase._groupSep = format.groupSeparator;
        NumEditBase._decSep = format.decimalSeparator;

        // Pole povolených kódů znaků
        const charArray = [43 /* + */, 45 /* - */, 13 /* Enter */, 0 /* Firefox ridici znaky: backspace, sipky, ...*/];

        if (NumberUtils.WHITESPACE_GROUP_SEPARATOR_REGEX.test(NumEditBase._groupSep)) {
            charArray.push(160);
            charArray.push(32);
        } else {
            charArray.push(NumEditBase._groupSep.charCodeAt(0));
        }

        NumEditBase._replaceGoupSep = new RegExp(NumEditBase._groupSep, "g");

        NumEditBase._requiredPatternWholeNumbers = new RegExp(`^(?:[+-])?(?:\\d+${NumEditBase._groupSep})*\\d*$`);
        NumEditBase._requiredPatternDecimal = new RegExp(`^(?:[+-])?(?:\\d+${NumEditBase._groupSep})*\\d*(?:\\${format.decimalSeparator}\\d+)?$`);

        NumEditBase._alowedCharsWholeNumbers = new Set(charArray);
        NumEditBase._alowedCharsDecimal = new Set(charArray);
        NumEditBase._alowedCharsDecimal.add(NumEditBase._decSep.charCodeAt(0));

        NumEditBase._formatsInit = true;
    }

    private static _onInput(event: Event): void {
        NumEditBase._validate(event.target as NumEditBase);
    }

    private static _validate(numedit: NumEditBase): void {
        //chtelo by to pretizit property validity a nastavit konkretni ValidityState
        let validateErrors = ValidateErrors.valid;
        let validityData: { validationMessage: string; validity: ValidityState } | null = null;
        if (numedit._wholeNumbers ? !NumEditBase._requiredPatternWholeNumbers.test(numedit._textValue.trim()) : !NumEditBase._requiredPatternDecimal.test(numedit._textValue.trim())) {
            validateErrors = ValidateErrors.badInput;
        } else if (numedit.value) {
            if (numedit.min && numedit.valueAsNumber < +numedit.min)
                validateErrors |= ValidateErrors.rangeUnderflow;
            if (numedit.max && numedit.valueAsNumber > +numedit.max)
                validateErrors |= ValidateErrors.rangeOverflow;

            validityData = ValidityStateHelper.createValidateState(validateErrors, numedit._wholeNumbers ? ValidatingDataType.integer : ValidatingDataType.float);
            validityData.validationMessage = validityData.validationMessage.replace(/\$\{\w+\}/, (str) =>
                Globalization.formatValue((numedit as any)[str.substr(2, str.length - 3)]));
        }

        if (validateErrors === ValidateErrors.valid) {
            numedit._validityState = null;
            numedit.setCustomValidity("");
        } else {
            if (!validityData)
                validityData = ValidityStateHelper.createValidateState(validateErrors, numedit._wholeNumbers ? ValidatingDataType.integer : ValidatingDataType.float);

            if (validityData) {
                numedit._validityState = validityData.validity;
                numedit.setCustomValidity(validityData.validationMessage);
            }
        }
    }

    /**
     * Nedovolí napsat nevalidní znaky
     */
    private static _onKeyPress(event: KeyboardEvent): boolean {
        // ctrl ridi zkratky a kontrolni prikazy, ale FF v preventDefault rusi vsechno tedy i zkratky samotneho FF, a nefungovalo tedy ani ctrl+v, takze pokud je pri klavese stisknuto
        // ctrl, asi by se meli vsechny povolit

        if (event.ctrlKey) {
            return true;
        }

        if (event.target instanceof NumEditBase) {
            const numEdit = event.target;
            const regexMatch = numEdit._textValue.match(/\d/);
            const containsDigit = regexMatch?.length > 0;

            if (event.key == ":" && numEdit._textValue.length < 2 && !containsDigit) {
                return false;
            }

            if (numEdit._textValue.includes(":")) {
                event.preventDefault();
                return false;
            }

            if (!((event.charCode >= 48 && event.charCode <= 57) ||
                (numEdit._wholeNumbers ? NumEditBase._alowedCharsWholeNumbers.has(event.charCode) : NumEditBase._alowedCharsDecimal.has(event.charCode))) ||
                (event.charCode == 32) ||
                (((event.charCode == 43) || (event.charCode == 45)) && ((numEdit.selectionStart != 0) || (numEdit._textValue.indexOf("+") >= 0) || (numEdit._textValue.indexOf("-") >= 0)))) {
                event.preventDefault();
                return false;
            }

            if (event.charCode == 13)
                numEdit.value = numEdit._textValue;
        }

        return true;
    }

    private static _onFocus(event: Event): void {
        const target = event.target as NumEditBase;
        if (!target.checkValidity())
            return;

        target._focused = true;

        // při focusu se ze zobrazované hodnoty odstraní oddělovače tisíců
        target._removeSeparator();
    }

    private static _onBlur(event: Event): void {
        const target = event.target as NumEditBase;
        target._textValue = target._textValue.replace(/:/, "");

        if (!target.checkValidity())
            return;

        target._focused = false;

        // při ztrátě focusu se naopak aplikuje plné formátování (i s oddělovači)
        target._addSeparator();
    }

    /**
     * Odebira oddelovace tisicu
     */
    private _removeSeparator(): void {
        //pokud je pouze pro cteni neprenastavuji text
        if (!this.readOnly) {
            if (Number.isNaN(this.valueAsNumber)) {
                this._textValue = "";
                this._originalValue = Number.NaN;
            } else {
                this._textValue = this._textValue.replace(NumEditBase._replaceGoupSep, "");
                this._originalValue = +this.value;
            }
        }
    }

    /**
     * Pridava oddelovace tisicu
     */
    private _addSeparator(): void {
        this.value = this._textValue;
    }

    /**
     * Odstraní formátování a vrátí string s číslem v neutrální kultuře.
     * V případě že je oddělovač tisíců mezera (dělitelná / nedělitelná), odstraní oba typy mezer.
     * @param value Řetězec ve kterém se odstraní formátování
     * @private
     */
    private static _removeFormatting(value: string): string {
        return NumberUtils.removeFormatting(value, NumEditBase._groupSep, NumEditBase._decSep);
    }

    private _processIncomingValue(value: string | number | null | undefined): void {
        if (value == null) {
            this._originalValue = Number.NaN;
            this.valueAsNumber = Number.NaN;
            NumEditBase._validate(this);
            return;
        }

        // pro případ, že by přišlo přímo číslo
        if (typeof value === "number") {
            this._originalValue = +value;
            this.valueAsNumber = +value;
            NumEditBase._validate(this);
            return;
        }

        if (typeof value === "object") { // BigNumber nebo jiný objekt
            const val = value as any;
            if (!val.toString)
                throw Error("Wrong object");
            value = NumberUtils.numberToString(val.toString(), {});
        }

        value = NumEditBase._removeFormatting(value);
        if (value === "") {
            this._originalValue = Number.NaN;
            this.valueAsNumber = Number.NaN;
            NumEditBase._validate(this);
            return;
        }

        this._originalValue = +value;
        //this.valueAsNumber = +value;
        if (typeof value === "string") {
            this._processIncomingValueAsNumber(value);
        } else {
            this._processIncomingValueAsAny(value);
        }

        NumEditBase._validate(this);
    }

    private _processIncomingValueAsNumber(value: number | string | null | undefined): void {
        if (value == null || value === "" || Number.isNaN(+value))
            this._textValue = "";
        else // pokud je nastavený display format, tak se aplikuje, jinak se použije strandardní konverze na string
            this._textValue = this._displayFormat ? NumberUtils.getNumberToStringFunction(this._displayFormat).format(+value) : NumberUtils.numberToString((+value).toString(), {});
    }

    private _processIncomingValueAsAny(value: number | string | object): void {
        if (value == null || value === "" || Number.isNaN(+value))
            this._textValue = "";
        else {
            this._textValue = this._displayFormat ? NumberUtils.getNumberToStringFunction(this._displayFormat).format(value) : NumberUtils.numberToString((value).toString(), {});
        }
    }
}

registerCustomElement("tsc-base-numedit", NumEditBase, "input");

// ukázka vytvoření dalšího potomka
/*export class NumEditBase2 extends NumEditBase {
    protected _init() {
        super._init();

        this.classList.add("num-edit-base2");
    }
}

TescoSW.UI.registerCustomElement("tsc-base-numedit2", NumEditBase2, 'input');*/
